import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  StatusChip,
  type StatusChipVariant,
} from '@/components/ui/status-chip';
import type { RecorderJob } from '@/sections/asset/components/tabs/credentials/RecorderProgress/useRecorderJobs';

interface RecorderStatusCellProps {
  recorder?: RecorderJob;
  // Opens the progress modal subscribed to this credential's recorder run.
  onOpen: (conversationId: string, label: string) => void;
}

// Per-credential recording status, derived entirely from the recorder-agent
// job: active → Recording…; failed → Failed (a hard failure AND a blocked
// give-up both fail the job now, so job status is the single source of truth);
// otherwise → Recorded. A credential with no recorder run (manual recipe, static
// token) renders nothing. Click opens the live/terminal timeline modal.
export const RecorderStatusCell: React.FC<RecorderStatusCellProps> = ({
  recorder,
  onOpen,
}) => {
  if (!recorder) return null;
  const { variant, label, icon } = present(recorder);
  return (
    <button
      type="button"
      className="cursor-pointer hover:opacity-80"
      onClick={() => onOpen(recorder.conversationId, recorder.label)}
    >
      <StatusChip variant={variant} label={label} icon={icon} />
    </button>
  );
};

function present(recorder: RecorderJob): {
  variant: StatusChipVariant;
  label: string;
  icon?: React.ReactNode;
} {
  if (recorder.isActive) return { variant: 'high', label: 'Recording…' };
  if (recorder.isFailed) {
    return { variant: 'critical', label: 'Failed', icon: <XCircleIcon /> };
  }
  return { variant: 'info', label: 'Recorded', icon: <CheckCircleIcon /> };
}
